import { useState } from 'react';
import { useBookingLinks, type BookingLink } from '../../../api/booking-links.api';

/* CalendarSlotPicker — Sprint 16a.
   The `calendar_slot` block's URL field. Two modes:
     - "From booking links": dropdown of the user's BookingLink rows; picking
       one writes that URL into the block (literal value, no token).
     - "Custom URL": free-text input fallback.
   The mode is inferred from whether the current value matches any of the
   user's link URLs (link match → "from links"; otherwise → "custom"). */

export interface CalendarSlotPickerProps {
  /** Current URL value on the calendar_slot block. */
  value: string;
  onChange: (next: string) => void;
}

export interface CalendarSlotPickerInnerProps extends CalendarSlotPickerProps {
  /** Links list passed in by the outer wrapper (or by tests). Pure inner —
   *  no hooks beyond local state so the unit test doesn't need a
   *  QueryClientProvider. */
  links: BookingLink[];
}

type Mode = 'links' | 'custom';

export function inferMode(value: string, links: BookingLink[]): Mode {
  // Empty → default to links mode (recruiter is about to pick one).
  if (!value) return 'links';
  // Treat any value that doesn't match a known link URL as custom — covers
  // the legacy `{{calendar_url}}` token and hand-pasted URLs.
  return links.some((l) => l.url === value) ? 'links' : 'custom';
}

/** Hook-aware outer wrapper used by the builder. */
export function CalendarSlotPicker(props: CalendarSlotPickerProps) {
  const query = useBookingLinks();
  return <CalendarSlotPickerInner {...props} links={query.data?.data ?? []} />;
}

/** Pure presentational inner — easy to unit-test. */
export function CalendarSlotPickerInner({ value, onChange, links }: CalendarSlotPickerInnerProps) {
  const [mode, setMode] = useState<Mode>(() => inferMode(value, links));

  const handleMode = (next: Mode) => {
    setMode(next);
    // Switching to "links" with no value picks the default link as a hint.
    if (next === 'links' && !value) {
      const def = links.find((l) => l.is_default) ?? links[0];
      if (def) onChange(def.url);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div role="group" aria-label="Calendar source" style={{ display: 'inline-flex', border: '1px solid var(--color-border-default, #ececf2)', borderRadius: 4, overflow: 'hidden' }}>
        <ModeBtn active={mode === 'links'} onClick={() => handleMode('links')} testid="cal-mode-links">
          From booking links
        </ModeBtn>
        <ModeBtn active={mode === 'custom'} onClick={() => handleMode('custom')} testid="cal-mode-custom">
          Custom URL
        </ModeBtn>
      </div>

      {mode === 'links' ? (
        links.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted, #6b6b7d)' }}>
            No booking links yet. Add some under{' '}
            <a href="/settings" style={{ color: 'var(--color-brand-blue, #3b5bdb)' }}>Settings → Booking links</a>.
          </p>
        ) : (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid="cal-link-select"
            style={selectStyle()}
          >
            {!links.some((l) => l.url === value) && (
              <option value="" disabled>
                Select a booking link…
              </option>
            )}
            {links.map((l) => (
              <option key={l.id} value={l.url}>
                {l.label}
                {l.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        )
      ) : (
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://cal.example/me/30min  or  {{calendar_url}}"
          data-testid="cal-custom-input"
          style={selectStyle()}
        />
      )}
    </div>
  );
}

function ModeBtn({
  active,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      style={{
        padding: '4px 10px',
        fontSize: 11,
        background: active ? 'var(--color-action-primary, #1e1b4b)' : '#fff',
        color: active ? '#fff' : 'var(--color-text-muted, #6b6b7d)',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function selectStyle(): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
  };
}
